import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ImagePlus, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", area: "", address: "", priceFrom: "", priceTo: "", image: "", featured: false };

const inputCls = "h-11 bg-white border-sand focus-visible:ring-gold rounded-none text-sm";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] font-semibold text-[#525D6B] mb-1.5";

export const DevelopmentForm = ({ open, onOpenChange, development, onSaved }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) setForm(development ? { ...development } : EMPTY);
  }, [open, development]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const token = localStorage.getItem("cv_admin_token");
      const { data } = await axios.post(`${API}/admin/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setForm((prev) => ({ ...prev, image: data.url }));
      toast.success(t.admin.photoUploaded);
    } catch {
      toast.error(t.form.error);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = localStorage.getItem("cv_admin_token");
    const payload = { ...form, priceFrom: Number(form.priceFrom), priceTo: Number(form.priceTo) };
    try {
      if (development?.id) {
        await axios.put(`${API}/admin/developments/${development.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/developments`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(t.admin.saved);
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error(t.form.error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="development-form-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-lenis-prevent>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ink">
            {development ? t.admin.editDevelopment : t.admin.addDevelopment}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div className="sm:col-span-2">
            <label className={labelCls}>{t.admin.devName} *</label>
            <Input data-testid="development-name-input" value={form.name} onChange={set("name")} className={inputCls} required />
          </div>
          <div>
            <label className={labelCls}>{t.admin.areaLabel} *</label>
            <Input data-testid="development-area-input" value={form.area} onChange={set("area")} className={inputCls} placeholder="Brickell" required />
          </div>
          <div>
            <label className={labelCls}>{t.admin.address}</label>
            <Input data-testid="development-address-input" value={form.address} onChange={set("address")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t.admin.priceFrom} *</label>
            <Input data-testid="development-price-from-input" type="number" min="0" value={form.priceFrom} onChange={set("priceFrom")} className={inputCls} required />
          </div>
          <div>
            <label className={labelCls}>{t.admin.priceTo}</label>
            <Input data-testid="development-price-to-input" type="number" min="0" value={form.priceTo} onChange={set("priceTo")} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>{t.admin.imageUrl} *</label>
            {form.image && (
              <img src={form.image} alt="" data-testid="development-image-preview" className="mb-3 w-full aspect-video object-cover border border-sand" />
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="development-file-input" />
            <button
              type="button"
              data-testid="development-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-gold/60 bg-gold-light/40 hover:bg-gold-light text-ink font-semibold text-sm px-6 py-4 transition-colors disabled:opacity-60"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4 text-gold-dark" />}
              {uploading ? t.admin.uploading : t.admin.uploadPhoto}
            </button>
            <p className="mt-3 mb-1.5 text-[10px] uppercase tracking-[0.2em] text-[#8C96A4]">{t.admin.orPasteUrl}</p>
            <Input data-testid="development-image-input" value={form.image} onChange={set("image")} className={inputCls} placeholder="https://…" required />
          </div>
          <div className="sm:col-span-2 flex items-center gap-3">
            <Switch data-testid="development-featured-switch" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
            <span className="text-sm text-[#525D6B]">{t.admin.featured}</span>
          </div>
          <div className="sm:col-span-2 flex gap-3 pt-2">
            <button data-testid="development-save-btn" type="submit" disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark disabled:opacity-60 text-ink font-semibold text-sm tracking-wide px-6 py-3.5 transition-colors">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.admin.save}
            </button>
            <button data-testid="development-cancel-btn" type="button" onClick={() => onOpenChange(false)} className="px-6 py-3.5 border border-sand text-sm font-semibold text-[#525D6B] hover:border-ink hover:text-ink transition-colors">
              {t.admin.cancel}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
